import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Pause, Play } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { SmartImage } from "../SmartImage";

const DURATION = 3000;

export const StoryViewer = ({ items, startIndex = 0, categoryName, onClose }) => {
  const { t, tc } = useLang();
  const [index, setIndex] = useState(startIndex);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef(Date.now());

  const next = useCallback(() => {
    setIndex((i) => {
      if (i + 1 >= items.length) { onClose(); return i; }
      return i + 1;
    });
  }, [items.length, onClose]);

  const prev = () => setIndex((i) => Math.max(0, i - 1));

  useEffect(() => {
    setProgress(0);
    startedAt.current = Date.now();
  }, [index]);

  useEffect(() => {
    if (paused) return;
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt.current;
      const ratio = Math.min(1, elapsed / DURATION);
      setProgress(ratio);
      if (ratio >= 1) next();
    }, 50);
    return () => clearInterval(tick);
  }, [paused, index, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, onClose]);

  const item = items[index];
  if (!item) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black flex items-center justify-center"
        data-testid="story-viewer"
      >
        <div className="relative w-full h-full sm:h-[92vh] sm:w-auto sm:aspect-[9/16] sm:rounded-2xl overflow-hidden bg-charcoal">
          {item.image ? (
            <motion.div
              key={item.slug}
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              <SmartImage src={item.image} variants={item.image_variants} sizes="(max-width: 640px) 100vw, 520px" baseVariant="card" alt={tc(item.title)} className="w-full h-full" loading="eager" />
            </motion.div>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-green to-charcoal" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/55" />

          {/* progress bars */}
          <div className="absolute top-3 inset-x-3 flex gap-1.5 z-20" data-testid="story-progress">
            {items.map((_, i) => (
              <span key={i} className="flex-1 h-[3px] rounded-full bg-white/30 overflow-hidden">
                <span
                  className="block h-full bg-white"
                  style={{ width: i < index ? "100%" : i === index ? `${progress * 100}%` : "0%" }}
                />
              </span>
            ))}
          </div>

          {/* header */}
          <div className="absolute top-7 inset-x-3 flex items-center gap-3 z-20">
            <span className="w-9 h-9 rounded-full p-[2px] bg-gradient-to-tr from-green-light to-gold shrink-0">
              <span className="block w-full h-full rounded-full overflow-hidden bg-charcoal">
                {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
              </span>
            </span>
            <div className="min-w-0">
              <div className="text-white text-xs font-semibold truncate">{categoryName}</div>
              <div className="text-white/60 text-[11px]">{item.date}</div>
            </div>
            <button
              onClick={() => setPaused((p) => !p)}
              data-testid="story-pause"
              aria-label="pause"
              className="ms-auto w-9 h-9 rounded-full text-white/80 hover:bg-white/15 flex items-center justify-center"
            >
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              data-testid="story-close"
              aria-label="close"
              className="w-9 h-9 rounded-full text-white/80 hover:bg-white/15 flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* tap zones */}
          <button onClick={prev} data-testid="story-prev" aria-label="previous" className="absolute inset-y-0 start-0 w-1/3 z-10" />
          <Link
            to={`/media/${item.slug}`}
            onClick={onClose}
            data-testid="story-open-article"
            aria-label={tc(item.title)}
            className="absolute inset-y-0 start-1/3 w-1/3 z-10"
          />
          <button onClick={next} data-testid="story-next" aria-label="next" className="absolute inset-y-0 end-0 w-1/3 z-10" />

          {/* caption */}
          <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 z-20">
            <Link to={`/media/${item.slug}`} onClick={onClose} data-testid="story-title-link">
              <h3 className="font-display text-white text-xl sm:text-2xl leading-snug hover:text-gold transition-colors" data-testid="story-title">{tc(item.title)}</h3>
            </Link>
            {tc(item.excerpt) && (
              <p className="text-white/75 text-xs sm:text-sm mt-2 leading-relaxed line-clamp-3">{tc(item.excerpt)}</p>
            )}
            <Link
              to={`/media/${item.slug}`}
              onClick={onClose}
              data-testid="story-read-more"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/95 text-charcoal text-[11px] font-semibold uppercase tracking-[0.14em] px-4 py-2.5 hover:bg-gold transition-colors"
            >
              {t.media.readMore} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
